import React, { useState, useEffect } from 'react';
import { rankingsService } from '../services/api';
import type { RankingRow, GuildRankingRow, LevelRankingRow } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { GuildRankingCard, GuildRankFX, getGuildMarkUrl, type GuildCardItem } from '../components/GuildRankingCard';
import { mergeGuildRankConfig } from '../utils/guildRankConfig';
import '../styles/Rankings.css';

type TabId = 'tower' | 'pk' | 'guild' | 'level';

const CLASS_DISPLAY: Record<string, string> = {
    '000': 'Guardian',
    '001': 'Berserker',
    '002': 'Assassin',
    '003': 'Archer',
    '004': 'Priest',
    '005': 'Trickster',
    '006': 'Magician',
    '007': 'Mage',
};

// Códigos de classe no Table_CharBase (jogo Icarus)
const LEVEL_CLASS_DISPLAY: Record<string, string> = {
    gd: 'Guardian',
    wr: 'Berserker',
    ac: 'Archer',
    pr: 'Priest',
    tf: 'Assassin',
    wz: 'Mage',
    mg: 'Magician',
    do: 'Idol',
};

const LEVEL_CLASS_IMAGE: Record<string, string> = {
    gd: '/assets/img/nav_guardian.jpg',
    wr: '/assets/img/nav_berserker.jpg',
    ac: '/assets/img/nav_ranger.jpg',
    pr: '/assets/img/nav_priest.jpg',
    tf: '/assets/img/nav_assassin.jpg',
    wz: '/assets/img/nav_wizard.jpg',
    mg: '/assets/img/nav_magician.jpg',
    do: '/assets/img/nav_trickster.jpg',
};

const MEDAL_IMAGE: Record<number, string> = {
    1: '/assets/images/rankings/trophy.svg',
    2: '/assets/images/rankings/medal-2.svg',
    3: '/assets/images/rankings/medal-3.svg',
};

function getClassDisplay(cls: string): string {
    return (CLASS_DISPLAY[cls] ?? cls) || '-';
}

function getLevelClassDisplay(cls: string): string {
    return (LEVEL_CLASS_DISPLAY[cls] ?? cls) || '-';
}

function getLevelClassImage(cls: string): string | undefined {
    return LEVEL_CLASS_IMAGE[cls];
}

interface PodiumItem {
    id: string;
    rank: number;
    name: string;
    subtitle: string;
    valueText: string;
    valueLabel: string;
    extra?: string;
    avatarUrl?: string;
    iconClass?: string;
}

const Rankings: React.FC = () => {
    const { t } = useLanguage();
    const { config: siteConfig } = useSiteConfig();
    const guildRank = mergeGuildRankConfig(siteConfig.guildRank);
    const [activeTab, setActiveTab] = useState<TabId>('tower');
    const [towerData, setTowerData] = useState<RankingRow[]>([]);
    const [pkData, setPkData] = useState<RankingRow[]>([]);
    const [guildData, setGuildData] = useState<GuildRankingRow[]>([]);
    const [levelData, setLevelData] = useState<LevelRankingRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const loadTower = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await rankingsService.getTower(100);
            setTowerData(Array.isArray(data) ? data : []);
        } catch (e) {
            setTowerData([]);
            setError(t('rankings.loadErrorTower'));
        } finally {
            setLoading(false);
        }
    };

    const loadPk = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await rankingsService.getPk(100);
            setPkData(Array.isArray(data) ? data : []);
        } catch (e) {
            setPkData([]);
            setError(t('rankings.loadErrorPk'));
        } finally {
            setLoading(false);
        }
    };

    const loadGuild = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await rankingsService.getGuild(100);
            setGuildData(Array.isArray(data) ? data : []);
        } catch (e) {
            setGuildData([]);
            setError(t('rankings.loadErrorGuild'));
        } finally {
            setLoading(false);
        }
    };

    const loadLevel = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await rankingsService.getLevel(10);
            setLevelData(Array.isArray(data) ? data : []);
        } catch (e) {
            setLevelData([]);
            setError(t('rankings.loadErrorLevel'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'tower') loadTower();
        else if (activeTab === 'pk') loadPk();
        else if (activeTab === 'guild') loadGuild();
        else if (activeTab === 'level') loadLevel();
    }, [activeTab]);

    const rankingTabs: { id: TabId; label: string; icon: string }[] = [
        { id: 'tower', label: t('rankings.tower'), icon: 'ri-building-line' },
        { id: 'pk', label: t('rankings.pk'), icon: 'ri-sword-line' },
        { id: 'guild', label: t('rankings.guild'), icon: 'ri-team-line' },
        { id: 'level', label: t('rankings.level'), icon: 'ri-user-star-line' },
    ];

    const getRankIcon = (rank: number) => {
        if (rank === 1) return '🥇';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return `#${rank}`;
    };

    let podiumItems: PodiumItem[] = [];
    let guildPodiumItems: GuildCardItem[] = [];
    if (activeTab === 'tower') {
        podiumItems = towerData.slice(0, 3).map((row) => ({
            id: `t-${row.charDBKey}`,
            rank: row.ranking,
            name: row.name,
            subtitle: getClassDisplay(row.class),
            valueText: Number(row.point).toLocaleString(),
            valueLabel: t('rankings.points'),
            extra: row.clearFloor ? `${t('rankings.floor')}: ${row.clearFloor}` : undefined,
            iconClass: 'ri-building-line',
        }));
    } else if (activeTab === 'pk') {
        podiumItems = pkData.slice(0, 3).map((row) => ({
            id: `p-${row.charDBKey}`,
            rank: row.ranking,
            name: row.name,
            subtitle: getClassDisplay(row.class),
            valueText: Number(row.point).toLocaleString(),
            valueLabel: t('rankings.points'),
            iconClass: 'ri-sword-line',
        }));
    } else if (activeTab === 'level') {
        podiumItems = levelData.slice(0, 3).map((row) => ({
            id: `l-${row.charDBKey}`,
            rank: row.rank,
            name: row.name,
            subtitle: getLevelClassDisplay(row.class),
            valueText: String(row.level),
            valueLabel: t('rankings.level'),
            extra: row.guildName || undefined,
            avatarUrl: getLevelClassImage(row.class),
        }));
    } else if (activeTab === 'guild') {
        guildPodiumItems = guildData.map((row) => ({
            rank: row.rank,
            name: row.name,
            guildMarkId: row.guildMarkId,
            grade: row.guildGrade,
            masterName: row.masterName,
            exploits: row.exploitsPoint,
        }));
        podiumItems = guildData.slice(0, 3).map((row) => ({
            id: `g-${row.dbKey}`,
            rank: row.rank,
            name: row.name,
            subtitle: `${t('rankings.grade')}: ${row.guildGrade}`,
            valueText: Number(row.exploitsPoint).toLocaleString(),
            valueLabel: t('rankings.exploits'),
            extra: row.masterName ? `${t('rankings.master')}: ${row.masterName}` : undefined,
            avatarUrl: getGuildMarkUrl(row.guildMarkId),
        }));
    }

    const podiumOrder = [2, 1, 3];
    const podiumCards = podiumOrder
        .map((r) => podiumItems.find((p) => p.rank === r))
        .filter((p): p is PodiumItem => Boolean(p));

    const hasData =
        activeTab === 'tower' ? towerData.length > 0 :
        activeTab === 'pk' ? pkData.length > 0 :
        activeTab === 'guild' ? guildData.length > 0 :
        levelData.length > 0;

    const listData = activeTab === 'tower' ? towerData.slice(3) :
        activeTab === 'pk' ? pkData.slice(3) :
        activeTab === 'guild' ? guildData.slice(3) :
        levelData.slice(3);

    return (
        <div className="rankings-page">
            <div className="container">
                <h1 className="page-title">
                    RANKINGS <span className="highlight">Knight Icarus Online</span>
                </h1>

                <div className="rankings-tabs">
                    {rankingTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <i className={tab.icon}></i>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="rankings-error">
                        {error}
                    </div>
                )}

                {loading && (
                    <div className="rankings-loading">
                        {t('rankings.loading')}
                    </div>
                )}

                {!loading && activeTab === 'guild' && guildPodiumItems.length > 0 && (
                    <div className="guild-cards">
                        {[2, 1, 3].map((r) => {
                            const item = guildPodiumItems.find((p) => p.rank === r);
                            if (!item) return null;
                            return <GuildRankingCard key={`g-${r}`} item={item} config={guildRank} />;
                        })}
                    </div>
                )}

                {!loading && activeTab === 'guild' && guildData.slice(3).length > 0 && (
                    <div className="rankings-table">
                        <div className="table-header">
                            <div className="rank-col">{t('rankings.rank')}</div>
                            <div className="name-col">{t('rankings.name')}</div>
                            <div className="class-col">{t('rankings.grade')}</div>
                            <div className="points-col">{t('rankings.exploits')}</div>
                            <div className="name-col">{t('rankings.master')}</div>
                        </div>
                        <div className="table-body">
                            {guildData.slice(3).map((row) => (
                                <div key={`g-${row.dbKey}`} className="table-row">
                                    <div className="rank-col"><span className="rank-icon">{getRankIcon(row.rank)}</span></div>
                                    <div className="name-col">
                                        <span className="guild-name-with-mark">
                                            <img className="guild-table-mark" src={getGuildMarkUrl(row.guildMarkId)} alt="" />
                                            <span className="player-name">{row.name}</span>
                                        </span>
                                    </div>
                                    <div className="class-col"><span className="class-badge">{row.guildGrade}</span></div>
                                    <div className="points-col"><span className="points-value">{Number(row.exploitsPoint).toLocaleString()}</span></div>
                                    <div className="name-col"><span className="player-name">{row.masterName || '-'}</span></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && activeTab !== 'guild' && podiumCards.length > 0 && (
                    <div className="rankings-podium">
                        {podiumCards.map((item) => {
                            const fx = activeTab === 'level' && item.rank === 1 && guildRank.effects.enabled
                                ? guildRank.effects
                                : null;
                            const crownOn = Boolean(fx && fx.crown);
                            return (
                                <div
                                    key={item.id}
                                    className={`podium-card place-${item.rank}${fx ? ' podium-fx-on' : ''}`}
                                >
                                    {!crownOn && (
                                        <div className="podium-medal">
                                            <img src={MEDAL_IMAGE[item.rank]} alt={`#${item.rank}`} />
                                        </div>
                                    )}
                                    {fx && <GuildRankFX fx={fx} />}
                                    <div className="podium-avatar">
                                        {item.avatarUrl
                                            ? <img src={item.avatarUrl} alt={item.subtitle} />
                                            : <i className={item.iconClass ?? 'ri-user-star-line'}></i>}
                                    </div>
                                    <div className="podium-rank">#{item.rank}</div>
                                    <div className="podium-name">{item.name}</div>
                                    <div className="podium-subtitle">{item.subtitle}</div>
                                    {item.extra && <div className="podium-extra">{item.extra}</div>}
                                    <div className="podium-value">
                                        {item.valueText}
                                        <span className="podium-value-label">{item.valueLabel}</span>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="podium-base">
                            <span className="podium-step step-2">2º</span>
                            <span className="podium-step step-1">1º</span>
                            <span className="podium-step step-3">3º</span>
                        </div>
                    </div>
                )}

                {!loading && activeTab === 'tower' && (
                    <div className="rankings-table">
                        <div className="table-header">
                            <div className="rank-col">{t('rankings.rank')}</div>
                            <div className="name-col">{t('rankings.name')}</div>
                            <div className="class-col">{t('rankings.class')}</div>
                            <div className="points-col">{t('rankings.points')}</div>
                            <div className="points-col">{t('rankings.floor')}</div>
                        </div>
                        <div className="table-body">
                            {!hasData && !error && <div className="table-empty">{t('rankings.noData')}</div>}
                            {towerData.slice(3).map((row) => (
                                <div key={`t-${row.charDBKey}`} className="table-row">
                                    <div className="rank-col"><span className="rank-icon">{getRankIcon(row.ranking)}</span></div>
                                    <div className="name-col"><span className="player-name">{row.name}</span></div>
                                    <div className="class-col"><span className="class-badge">{getClassDisplay(row.class)}</span></div>
                                    <div className="points-col"><span className="points-value">{Number(row.point).toLocaleString()}</span></div>
                                    <div className="points-col"><span className="points-value">{row.clearFloor ?? 0}</span></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && activeTab === 'pk' && (
                    <div className="rankings-table">
                        <div className="table-header">
                            <div className="rank-col">{t('rankings.rank')}</div>
                            <div className="name-col">{t('rankings.name')}</div>
                            <div className="class-col">{t('rankings.class')}</div>
                            <div className="points-col">{t('rankings.points')}</div>
                        </div>
                        <div className="table-body">
                            {!hasData && !error && <div className="table-empty">{t('rankings.noData')}</div>}
                            {pkData.slice(3).map((row) => (
                                <div key={`p-${row.charDBKey}`} className="table-row">
                                    <div className="rank-col"><span className="rank-icon">{getRankIcon(row.ranking)}</span></div>
                                    <div className="name-col"><span className="player-name">{row.name}</span></div>
                                    <div className="class-col"><span className="class-badge">{getClassDisplay(row.class)}</span></div>
                                    <div className="points-col"><span className="points-value">{Number(row.point).toLocaleString()}</span></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && activeTab === 'level' && (
                    <div className="rankings-table">
                        <div className="table-header">
                            <div className="rank-col">{t('rankings.rank')}</div>
                            <div className="name-col">{t('rankings.name')}</div>
                            <div className="class-col">{t('rankings.class')}</div>
                            <div className="level-col">{t('rankings.level')}</div>
                            <div className="name-col">{t('rankings.guildName')}</div>
                        </div>
                        <div className="table-body">
                            {!hasData && !error && <div className="table-empty">{t('rankings.noData')}</div>}
                            {levelData.slice(3).map((row) => (
                                <div key={`l-${row.charDBKey}`} className="table-row">
                                    <div className="rank-col"><span className="rank-icon">{getRankIcon(row.rank)}</span></div>
                                    <div className="name-col"><span className="player-name">{row.name}</span></div>
                                    <div className="class-col"><span className="class-badge">{getLevelClassDisplay(row.class)}</span></div>
                                    <div className="level-col"><span className="level-badge">{row.level}</span></div>
                                    <div className="name-col"><span className="player-name">{row.guildName || '-'}</span></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && activeTab === 'guild' && guildPodiumItems.length === 0 && !error && (
                    <div className="table-empty">{t('rankings.noData')}</div>
                )}

                <div className="rankings-info">
                    <div className="info-card">
                        <h3>Como funcionam os rankings</h3>
                        <ul>
                            <li><strong>Torre Infinita:</strong> Pontos e andar máximo alcançado nesta semana. Rankings são atualizados semanalmente.</li>
                            <li><strong>PvP (PK):</strong> Pontos de combate PvP desta semana. Baseado em vitórias e desempenho em batalhas.</li>
                            <li><strong>Level:</strong> Os personagens de maior nível, desempate pela experiência. Contas de GM não aparecem.</li>
                            <li><strong>Guilds:</strong> Ordenado por pontos de exploração (Exploits). Quanto maior a participação da guild em conteúdo, maior a pontuação.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Rankings;